import { NETWORK_RECIPIENTS_LABELS, NetworkType, NotificationService, type RecipientTypes } from '@capsicum/common';
import {
	Card,
	DeleteRecipientModal,
	ModalService,
	RecipientForm,
	type RecipientFormShape,
	RecipientFormSkeleton,
	Snackbar,
	useRouter
} from '@capsicum/common-web';
import { useNCWRecipientEdit } from '@capsicum/features/nc-wallet/shared';
import { useWalletWithdrawEditRecipientMutation, useWalletWithdrawRecipient } from '@capsicum/features/wallet/shared';
import { useParams } from 'next/navigation';
import styled from 'styled-components';

export const NCWEditRecipient = () => {
	const { back } = useRouter();
	const params = useParams();

	const recipientId = (params?.recipientId || '') as string;

	const {
		data: recipient,
		isFetching: isUpdateRecipientFetching,
		refetch: updateRecipient
	} = useWalletWithdrawRecipient(recipientId);

	const { isPending: isEditRecipientLoading, mutate: saveRecipient } = useWalletWithdrawEditRecipientMutation();

	const { intlText, snackbarProps } = useNCWRecipientEdit();

	const onFormSubmission = (data: RecipientFormShape) => {
		saveRecipient(
			{
				id: recipientId,
				payload: {
					label: data.recipientName.trim(),
					type: data.type,
					walletOrExchangeName: data.exchange
				}
			},
			{
				onSuccess: () => {
					updateRecipient();
					back();
					NotificationService.addNotification({
						component: <Snackbar {...snackbarProps.editSuccessSnackbarProp} />
					});
				}
			}
		);
	};

	const onDeleteRecipient = () => {
		ModalService.addModal({
			renderComponent: ({ close }) => (
				<DeleteRecipientModal
					testID="NCWallet-withdraw-deleteRecipient-modal"
					onButtonPress={() => {
						saveRecipient(
							{
								id: recipientId,
								payload: {
									label: recipient?.label,
									status: 'disabled',
									type: recipient?.type
								}
							},
							{
								onSuccess: () => {
									close();
									back();
									NotificationService.addNotification({
										component: <Snackbar {...snackbarProps.deleteSuccessSnackbarProp} />
									});
								}
							}
						);
					}}
					onSecondaryActionOnButtonPress={close}
				/>
			)
		});
	};

	return (
		<S.Container>
			<S.Title>{intlText.title}</S.Title>
			<S.EditRecipientCard>
				{isUpdateRecipientFetching ? (
					<RecipientFormSkeleton />
				) : (
					<RecipientForm
						alertText={intlText.alert}
						defaultValues={{
							address: recipient?.blockchainAddress,
							exchange: recipient?.exchange,
							network: NETWORK_RECIPIENTS_LABELS[NetworkType.CC2],
							recipientName: recipient?.label,
							type: recipient?.type as RecipientTypes
						}}
						isEdit
						isSaving={isEditRecipientLoading || isUpdateRecipientFetching}
						networkType={NetworkType.CC2}
						secondaryButtonLabel={intlText.cancel}
						submitButtonLabel={intlText.createButtonLabel}
						onSecondaryClick={onDeleteRecipient}
						onSubmitForm={onFormSubmission}
					/>
				)}
			</S.EditRecipientCard>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
		padding: ${({ theme }) => theme.spacings.medium};
	`;

	export const EditRecipientCard = styled(Card)`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.medium};
		max-width: 100%;
	`;
	export const Title = styled.p`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: 0 0 ${({ theme }) => theme.spacings.medium};
	`;
}
